<script setup lang="ts">
import { type IProps, EEXPIRED_TIME, ECOOKIE_TYPE } from './policy-table.types'
import { useTableConfig } from './useTableConfig'

const { COLUMNS } = useTableConfig()
const expand = ref(false)

defineProps<IProps>()
</script>

<template>
  <div class="policy-table">
    <div
      :class="[
        'policy-table__container',
        !expand && 'policy-table__container--hide'
      ]"
    >
      <div class="policy-table__tableContainer">
        <div class="policy-table__thead">
          <div
            v-for="column in COLUMNS"
            :key="column.name"
            class="policy-table__th"
          >
            <p v-html="column.name" />
          </div>
        </div>
        
        <div>
          <div
            v-for="row in data"
            :key="row.name"
            class="policy-table__table"
          >
            <div class="policy-table__td policy-table__td--first">
              <p
                v-html="row.name"
              />
          
              <ul
                v-if="row.acts"
                class="policy-table__list"
              >
                <li
                  v-for="(act, index) in row.acts"
                  :key="index"
                  class="policy-table__item"
                >
                  <a
                    v-if="act.link"
                    class="policy-table__link"
                    target="_blank"
                    :href="act.link"
                  >
                    {{ act.name }}
                  </a>
      
                  <div
                    v-else
                    v-html="act.link"
                  />
                </li>
              </ul>
            </div>
        
            <div class="policy-table__nested">
              <template
                v-for="(cookie, index) in row.cookies"
                :key="index"
              >
                <div
                  class="policy-table__td"
                  v-html="cookie.name"
                />
                
                <div class="policy-table__td">
                  {{ $t(`${EEXPIRED_TIME[cookie.expired as unknown as keyof typeof EEXPIRED_TIME]}`) }}
                </div>
      
                <div class="policy-table__td policy-table__td--last">
                  <p>
                    {{ $t(`${ECOOKIE_TYPE[cookie.type as unknown as keyof typeof ECOOKIE_TYPE]}`) }}
                  </p>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <button
      class="policy-table__button"
      @click="expand = !expand"
    >
      {{ expand ? $t('common.closeMore') : $t('common.showMore') }}
      <img
        :class="[
          'policy-table__icon',
          expand && 'policy-table__icon--rotate'
        ]"
        src="/icons/arrow_down.svg"
      >
    </button>
  </div>
</template>

<style lang="scss" scoped>
.policy-table {
  display: flex;
  display: inline-block;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: auto;
  overflow: hidden;
  
  &__container {
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    
    &--hide {
      height: 40vh;
    }
  }
  
  &__tableContainer {
    width: 131rem;
    border-top: 1px solid $gray-300;
    border-right: 1px solid $gray-300;
    border-left: 1px solid $gray-300;
    border-radius: .8rem;
  }

  &__thead {
    position: sticky;
    top: 0;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }

  &__th {
    @include body-3-medium;
    display: flex;
    align-items: center;
    height: 6.4rem;
    padding-right: 2.4rem;
    padding-left: 2.4rem;
    background-color: $gray-300;
    border-left: 1px solid $gray-400;

    &:first-child {
      border-left: 0;
    }
  }

  &__td {
    @include body-2-medium;
    min-height: 6.4rem;
    padding: 2.4rem;
    border-bottom: 1px solid $gray-300;
    border-left: 1px solid $gray-300;

    &--first {
      border-left: 0;
    }

    &--last {
      border-right: 0;
      border-left: 1px solid $gray-300;
    }
  }

  &__table {
    display: grid;
    grid-template-columns: 1fr 3fr;
  }

  &__nested {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
  }

  &__link {
    color: $purple-400;
    text-decoration: underline;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    padding-top: 1.2rem;
  }

  &__button {
    @include body-2-medium;
    position: sticky;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1.2rem;
    width: 100%;
    padding: 1.8rem 0;
    color: $text-400;
    background-color: #dee6e6;
    border-radius: 0 0 8px 8px;
    cursor: pointer;
  }

  &__icon {
    transform: rotate(0);
    transition: transform 0.3s;

    &--rotate {
      transform: rotate(180deg)
    }
  }

  :deep(.light) {
    color: $text-300;
  }

  @include atM {
    &__container {
      width: auto;
    }
  }
}
</style>

